import { Link } from 'react-router-dom';
import { Phone, CalendarCheck, Star, ShieldCheck } from '@phosphor-icons/react';
import { BRAND } from '../lib/brand';

/**
 * HeroVariant — Reusable hero with Nashville imagery only (no India headshot).
 * India's photo lives BELOW the hero in `<AboutBelowHero />` for all variants.
 *
 * Props:
 *  - variant: 'v1' | 'v2' | 'v3' — drives styling + copy mood
 *  - bg: { image, fallback?, label, caption }
 *  - eyebrow, headlineLines (array), subline
 */
export const HeroVariant = ({ variant = 'v1', bg, eyebrow, headlineLines, subline }) => {
  // Per-variant overlay & accent treatment
  const overlays = {
    v1: 'bg-gradient-to-br from-[#1A0000]/55 via-[#800000]/30 to-black/30',
    v2: 'bg-gradient-to-br from-black/80 via-black/55 to-[#800000]/55',
    v3: 'bg-gradient-to-t from-[#1A0000]/90 via-[#1A0000]/55 to-[#1A0000]/30',
  };
  const heightClass = variant === 'v3' ? 'min-h-[88vh]' : 'min-h-[92vh]';

  return (
    <section
      data-testid={`hero-section-${variant}`}
      className={`relative ${heightClass} flex items-center overflow-hidden pt-20`}
    >
      {/* Nashville background */}
      <img
        src={bg.image}
        alt={`${bg.label} — Nashville, Tennessee`}
        loading="eager"
        onError={(e) => {
          if (bg.fallback && e.currentTarget.src !== bg.fallback) {
            e.currentTarget.src = bg.fallback;
          }
        }}
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className={`absolute inset-0 ${overlays[variant]}`} />

      {/* Decorative gold ring (variant 1 + 2 only) */}
      {variant !== 'v3' && (
        <div className="absolute top-32 right-10 hidden lg:block opacity-15 pointer-events-none">
          <svg width="220" height="220" viewBox="0 0 220 220" fill="none">
            <circle cx="110" cy="110" r="109" stroke="#D4AF37" strokeWidth="1" />
            <circle cx="110" cy="110" r="80" stroke="#D4AF37" strokeWidth="0.5" />
            <circle cx="110" cy="110" r="50" stroke="#D4AF37" strokeWidth="0.5" />
          </svg>
        </div>
      )}

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 w-full">
        <div className={`max-w-4xl ${variant === 'v2' ? 'mx-auto text-center' : ''}`}>
          <div className={`inline-flex items-center gap-3 mb-7 ${variant === 'v2' ? 'mx-auto' : ''}`}>
            <div className="h-px w-10 bg-[#D4AF37]" />
            <span className="text-[#D4AF37] text-xs font-semibold tracking-[0.3em] uppercase">
              {eyebrow}
            </span>
            <div className="h-px w-10 bg-[#D4AF37]" />
          </div>

          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-7xl font-semibold text-white leading-[1.05] mb-6 drop-shadow-2xl">
            {headlineLines.map((line, i) => (
              <span key={i} className={i === 0 ? '' : 'block mt-2'}>
                {line.italic ? (
                  <span className="italic font-light text-3xl sm:text-4xl lg:text-5xl text-white/95">{line.text}</span>
                ) : line.shimmer ? (
                  <span className="gold-shimmer">{line.text}</span>
                ) : (
                  line.text
                )}
              </span>
            ))}
          </h1>

          <p className={`text-base sm:text-lg lg:text-xl text-white/95 max-w-2xl leading-relaxed mb-10 drop-shadow-lg ${variant === 'v2' ? 'mx-auto' : ''}`}>
            {subline}
          </p>

          <div className={`flex flex-col sm:flex-row gap-4 mb-12 ${variant === 'v2' ? 'justify-center' : ''}`}>
            <Link
              to="/booking"
              data-testid={`hero-${variant}-book-now-btn`}
              className="group inline-flex items-center justify-center gap-3 bg-[#D4AF37] text-[#1A1A1A] hover:bg-[#F5E6A8] transition-all duration-300 px-8 py-4 rounded-sm font-semibold text-base tracking-wide uppercase shadow-2xl hover:shadow-[#D4AF37]/40 hover:-translate-y-0.5"
            >
              <CalendarCheck weight="bold" size={22} />
              Book Your Appointment
            </Link>
            <a
              href={`tel:${BRAND.phoneRaw}`}
              data-testid={`hero-${variant}-call-btn`}
              className="inline-flex items-center justify-center gap-3 border border-white/60 backdrop-blur-sm bg-white/10 text-white hover:bg-white hover:text-[#800000] hover:border-white transition-all duration-300 px-8 py-4 rounded-sm font-semibold text-base"
            >
              <Phone weight="bold" size={22} />
              {BRAND.phone}
            </a>
          </div>

          <div className={`flex flex-wrap items-center gap-x-8 gap-y-4 pt-6 border-t border-white/20 ${variant === 'v2' ? 'justify-center' : ''}`}>
            <div className="flex items-center gap-2">
              {[...Array(5)].map((_, i) => (
                <Star key={i} weight="fill" size={18} className="text-[#D4AF37]" />
              ))}
              <span className="text-white text-sm ml-1 font-medium">5.0 Google</span>
            </div>
            <div className="flex items-center gap-2 text-white text-sm">
              <ShieldCheck weight="fill" size={18} className="text-[#D4AF37]" />
              <span>Bonded & Commissioned</span>
            </div>
            <div className="text-white text-sm font-medium">
              <span className="text-[#D4AF37] font-bold">MBA</span> · Nashville Native
            </div>
          </div>
        </div>
      </div>

      {/* Image caption (bottom-right) */}
      <div className="absolute bottom-6 right-6 z-10 hidden md:block">
        <p className="text-[10px] tracking-[0.25em] uppercase text-white/55 italic">
          {bg.caption}
        </p>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-white to-transparent pointer-events-none" />
    </section>
  );
};
